import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// NOTE: English version temporarily disabled
// Files preserved for future re-enable
// To re-enable: remove ENGLISH_DISABLED flag and restore language detection

const ENGLISH_DISABLED = true; // Set to false to re-enable English

// Supported languages
export const LANGUAGES = {
  id: { code: 'id', name: 'Bahasa', flag: '🇮🇩', locale: 'id-ID' },
  en: { code: 'en', name: 'English', flag: '🇺🇸', locale: 'en-US' }
};

// Route mappings between languages (NEW URL STRUCTURE)
export const ROUTE_MAP = {
  id: {
    home: '/',
    calculators: '/calculators',
    investasi: '/calculators/investment/compound-interest-calculator',
    danadarurat: '/calculators/savings/emergency-fund-calculator',
    kpr: '/calculators/property/kpr-calculator',
    asuransi: '/calculators/insurance/life-insurance-calculator',
    aibudget: '/ai-tools/budget-planner',
    news: '/news',
    premium: '/premium'
  },
  en: {
    home: '/en',
    calculators: '/en/calculators',
    investasi: '/en/calculators/investment/compound-interest-calculator',
    danadarurat: '/en/calculators/savings/emergency-fund-calculator',
    kpr: '/en/calculators/property/kpr-calculator',
    asuransi: '/en/calculators/insurance/life-insurance-calculator',
    aibudget: '/en/ai-tools/budget-planner',
    news: '/news',
    premium: '/en/premium'
  }
};

// Reverse route mapping for detecting current page
export const REVERSE_ROUTE_MAP = {
  '/': { page: 'home', lang: 'id' },
  '/calculators': { page: 'calculators', lang: 'id' },
  '/calculators/investment/compound-interest-calculator': { page: 'investasi', lang: 'id' },
  '/calculators/savings/emergency-fund-calculator': { page: 'danadarurat', lang: 'id' },
  '/calculators/property/kpr-calculator': { page: 'kpr', lang: 'id' },
  '/calculators/insurance/life-insurance-calculator': { page: 'asuransi', lang: 'id' },
  '/ai-tools/budget-planner': { page: 'aibudget', lang: 'id' },
  '/news': { page: 'news', lang: 'id' },
  '/premium': { page: 'premium', lang: 'id' },
  '/en': { page: 'home', lang: 'en' },
  '/en/calculators': { page: 'calculators', lang: 'en' },
  '/en/calculators/investment/compound-interest-calculator': { page: 'investasi', lang: 'en' },
  '/en/calculators/savings/emergency-fund-calculator': { page: 'danadarurat', lang: 'en' },
  '/en/calculators/property/kpr-calculator': { page: 'kpr', lang: 'en' },
  '/en/calculators/insurance/life-insurance-calculator': { page: 'asuransi', lang: 'en' },
  '/en/ai-tools/budget-planner': { page: 'aibudget', lang: 'en' },
  '/en/news': { page: 'news', lang: 'en' },
  '/en/premium': { page: 'premium', lang: 'en' }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Always use Indonesian when English is disabled
  const [language, setLanguageState] = useState('id');
  
  // Redirect /en/* routes to Indonesian equivalents when English is disabled
  useEffect(() => {
    if (ENGLISH_DISABLED && location.pathname.startsWith('/en')) {
      // Strip /en prefix and redirect
      const indonesianPath = location.pathname.replace(/^\/en/, '') || '/';
      navigate(indonesianPath, { replace: true });
    }
  }, [location.pathname, navigate]);
  
  // Get current page key from pathname
  const getCurrentPage = () => {
    const routeInfo = REVERSE_ROUTE_MAP[location.pathname];
    return routeInfo?.page || 'home';
  };
  
  // Switch language (disabled when ENGLISH_DISABLED is true)
  const setLanguage = (newLang) => {
    if (ENGLISH_DISABLED) {
      // Force Indonesian when English is disabled
      return;
    }
    
    if (newLang === language) return;
    
    localStorage.setItem('kalkulatorduit_lang', newLang);
    setLanguageState(newLang);
    
    // Navigate to equivalent page in new language
    const currentPage = getCurrentPage();
    const newRoute = ROUTE_MAP[newLang][currentPage] || ROUTE_MAP[newLang].home;
    navigate(newRoute);
  };
  
  // Get localized route
  const getRoute = (pageKey) => {
    return ROUTE_MAP[language][pageKey] || ROUTE_MAP[language].home;
  };
  
  // Get alternate language URL for current page (for hreflang)
  const getAlternateURL = (lang) => {
    const currentPage = getCurrentPage();
    return ROUTE_MAP[lang][currentPage] || ROUTE_MAP[lang].home;
  };
  
  const value = {
    language,
    setLanguage,
    getRoute,
    getAlternateURL,
    getCurrentPage,
    isEnglish: false, // Always false when English is disabled
    isIndonesian: true, // Always true when English is disabled
    langInfo: LANGUAGES['id'],
    englishDisabled: ENGLISH_DISABLED
  };
  
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
